import { BadgeProps } from "ui/components/Badge/types";
import {
  ContractEditFormValues,
  ContractRentSearchFormValues,
  ContractSaleSearchFormValues,
  ContractSearchFormValues,
} from "./types";

/*-------------------------------------------------------------------------------------------
  VEHICLE CONTRACT
---------------------------------------------------------------------------------------------*/
export const initialContractSearchForm: ContractSearchFormValues =
  {
    search: "",
  };

export const initialContractSaleSearchForm: ContractSaleSearchFormValues =
  {
    search: null,
    contractSaleStatusId: null,
    contractDateFrom: null,
    contractDateTo: null,
    sellingPriceMin: null,
    sellingPriceMax: null,
    contactClientId: null,
  };

export const initialContractRentSearchForm: ContractRentSearchFormValues =
  {
    search: null,
    contractRentStatusId: null,
    contractDateFrom: null,
    contractDateTo: null,
    sellingPriceMin: null,
    sellingPriceMax: null,
    contactClientId: null,
  };

export const initialContractEditForm: ContractEditFormValues = {
  name: null,
};

/**
 * Mapiranje statusa ugovora na badge variant prema fazi pipeline-a (sale i rent dijele iste ID-eve).
 * draft/created = pasivno (sivo), signed = u tijeku (plavo), invoiced = uspješno (zeleno), cancelled = neuspjeh (crveno).
 */
export const getContractStatusBadgeVariant = (statusId: string | null | undefined): BadgeProps["variant"] => {
  switch (statusId) {
    case "draft":
      return "secondary";
    case "created":
      return "violet";
    case "signed":
      return "info";
    case "invoiced":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return "secondary";
  }
};

/**
 * Tailwind klasa za bar/circle indikator po statusu ugovora.
 */
export const getContractStatusBarColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "draft":
      return "bg-slate-300";
    case "created":
      return "bg-slate-300";
    case "signed":
      return "bg-blue-400";
    case "invoiced":
      return "bg-green-500";
    case "cancelled":
      return "bg-red-400";
    default:
      return "bg-slate-300";
  }
};

// Redoslijed statusa ugovora u pipeline-u. Nepoznati idu na kraj.
export const CONTRACT_STATUS_ORDER = ["draft", "created", "signed", "invoiced", "cancelled"];
export const contractStatusRank = (id: string) => {
  const i = CONTRACT_STATUS_ORDER.indexOf(id);
  return i === -1 ? 999 : i;
};

/**
 * Prozirni gradient ispune po statusu — okrenut (jak na kraju, uz vertikalni border) -> blijedi prema početku.
 * Boje: draft = slate, created = violet, signed = sky, invoiced = emerald, cancelled = rose. Pune klase za Tailwind JIT.
 */
export const getContractStatusBarFill = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "signed":
      return "bg-gradient-to-l from-sky-500/25 to-sky-500/[0.02] border-r-2 border-sky-500 group-hover/row:from-sky-500/40";
    case "invoiced":
      return "bg-gradient-to-l from-emerald-500/30 to-emerald-500/[0.02] border-r-2 border-emerald-500 group-hover/row:from-emerald-500/45";
    case "cancelled":
      return "bg-gradient-to-l from-rose-500/25 to-rose-500/[0.02] border-r-2 border-rose-500 group-hover/row:from-rose-500/40";
    case "created":
      return "bg-gradient-to-l from-violet-400/30 to-violet-400/[0.02] border-r-2 border-violet-400 group-hover/row:from-violet-400/45";
    case "draft":
    default:
      return "bg-gradient-to-l from-slate-400/30 to-slate-400/[0.02] border-r-2 border-slate-400 group-hover/row:from-slate-400/45";
  }
};

/**
 * Boja iznosa po statusu — samo terminalni ishodi (invoiced = zeleno, cancelled = crveno), ostalo neutralno.
 */
export const getContractStatusAmountColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "invoiced":
      return "text-emerald-600";
    case "cancelled":
      return "text-rose-600";
    default:
      return "text-slate-800";
  }
};

/**
 * Boja ringa (hover) po statusu — usklađena s bojom ispune. Pune klase za Tailwind JIT.
 */
export const getContractStatusRingColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "signed":
      return "hover:ring-sky-400";
    case "invoiced":
      return "hover:ring-emerald-400";
    case "cancelled":
      return "hover:ring-rose-400";
    case "created":
      return "hover:ring-violet-400";
    case "draft":
    default:
      return "hover:ring-slate-400";
  }
};
